import React from 'react';

import type { FallbackProps } from 'react-error-boundary';

import { Alert, Space } from 'antd';
import { AlertCircle } from 'lucide-react';

import { Button } from '@/shared/ui/Button';

import styles from './ErrorFallback.module.css';

export type ErrorFallbackProps = Partial<FallbackProps> & {
  title?: string;
  description?: string;
  className?: string;
  variant?: 'default' | 'compact';
  height?: number | string;
};

/**
 * 에러 발생 시 표시되는 기본 폴백 컴포넌트
 * 에러 메시지와 재시도/홈으로 버튼을 제공
 *
 * @example
 * ```tsx
 * <ErrorBoundary FallbackComponent={ErrorFallback}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * @example compact 버전
 * ```tsx
 * <ErrorBoundary
 *   FallbackComponent={(props) => (
 *     <ErrorFallback {...props} variant="compact" height={32} />
 *   )}>
 *   <CompactComponent />
 * </ErrorBoundary>
 * ```
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  title = '오류가 발생했습니다',
  description,
  className,
  variant = 'default',
  height,
}) => {
  const handleRetry = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }
  };

  const errorMessage = description || error?.message || '알 수 없는 오류가 발생했습니다.';

  if (variant === 'compact') {
    const style: React.CSSProperties = height ? { height } : {};

    return (
      <div className={`${styles.errorStateCompact} ${className || ''}`} style={style}>
        <div className={styles.compactContent}>
          <AlertCircle size={16} className={styles.errorIcon} />
          <span className={styles.errorMessage}>{errorMessage}</span>
        </div>
        <Button variant="filled" color="standard" onClick={handleRetry} className={styles.retryButton}>
          다시 시도
        </Button>
      </div>
    );
  }

  const defaultStyle: React.CSSProperties = height ? { minHeight: height } : {};

  return (
    <div className={`${styles.errorState} ${className || ''}`} style={defaultStyle}>
      <Alert
        message={title}
        description={
          <div>
            <p className={styles.errorDescription}>{errorMessage}</p>
            <Space>
              <Button variant="filled" color="standard" onClick={handleRetry} className={styles.retryButton}>
                다시 시도
              </Button>
            </Space>
          </div>
        }
        type="error"
        showIcon
      />
    </div>
  );
};
